import React, { useState, useEffect } from "react";
import { fetchQuotes, markQuoteRead, deleteQuote, resolveImg } from "../../services/api";
import { Button } from "../ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";
import { Loader2, Trash2, Mail, Phone, Globe, Package, Check, Inbox } from "lucide-react";

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [delTarget, setDelTarget] = useState(null);
  const { toast } = useToast();

  const load = () => { setLoading(true); fetchQuotes().then(setQuotes).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const markRead = async (q) => { try { await markQuoteRead(q.id); load(); } catch (e) {} };
  const doDelete = async () => { try { await deleteQuote(delTarget.id); toast({ title: "Deleted" }); setDelTarget(null); load(); } catch (e) {} };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" /></div>;

  return (
    <div>
      <h2 className="font-display font-bold text-lg text-[#14171c] mb-5">Quote Requests ({quotes.length})</h2>
      {quotes.length === 0 ? (
        <div className="bg-white rounded-xl border border-neutral-200 py-16 text-center text-neutral-400">
          <Inbox className="w-10 h-10 mx-auto mb-3" /> No quote requests yet.
        </div>
      ) : (
        <div className="space-y-3">
          {quotes.map((q) => (
            <div key={q.id} className={`bg-white rounded-xl border p-5 ${q.read ? "border-neutral-200" : "border-[#C9A227]/50 ring-1 ring-[#C9A227]/20"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-[#14171c]">{q.name}</span>
                    {q.company && <span className="text-sm text-neutral-500">• {q.company}</span>}
                    {!q.read && <span className="text-[10px] bg-[#C9A227] text-white px-2 py-0.5 rounded-full font-bold uppercase">New</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-neutral-600 mt-2">
                    <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#C9A227]" /> {q.email}</span>
                    {q.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#C9A227]" /> {q.phone}</span>}
                    {q.country && <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-[#C9A227]" /> {q.country}</span>}
                    {q.productCategory && <span className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-[#C9A227]" /> {q.productCategory}</span>}
                  </div>
                  {(q.quantity || q.eventDate) && (
                    <div className="text-sm text-neutral-500 mt-1">
                      {q.quantity && <span>Qty: {q.quantity} </span>}
                      {q.eventDate && <span>• Event: {q.eventDate}</span>}
                    </div>
                  )}
                  <p className="text-sm text-neutral-700 mt-2 bg-neutral-50 rounded-lg p-3">{q.message}</p>
                  {q.designImage && (
                    <div className="mt-3 flex items-start gap-3">
                      <img src={resolveImg(q.designImage)} alt="Design" className="w-24 h-28 object-contain rounded-lg border border-neutral-200 bg-neutral-50" />
                      {q.design && (
                        <div className="text-xs text-neutral-600 bg-[#C9A227]/10 rounded-lg p-2.5">
                          <div className="font-bold text-[#C9A227] uppercase mb-1">Custom Design</div>
                          {Object.entries(q.design).filter(([k]) => k !== "logo").map(([k, v]) => (
                            <div key={k}><span className="capitalize text-neutral-500">{k}:</span> {String(v)}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {q.fileName && <div className="text-xs text-neutral-400 mt-1">Attachment: {q.fileName}</div>}
                  <div className="text-xs text-neutral-400 mt-2">{q.created_at ? new Date(q.created_at).toLocaleString() : ""}</div>
                </div>
                <div className="flex flex-col gap-2">
                  {!q.read && <Button size="sm" variant="outline" onClick={() => markRead(q)} className="h-8 text-xs"><Check className="w-3.5 h-3.5 mr-1" /> Read</Button>}
                  <Button size="sm" variant="outline" onClick={() => setDelTarget(q)} className="h-8 w-8 p-0 text-red-500 border-red-200"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete request?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the quote request from {delTarget?.name}.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminQuotes;
